/**
 * W-8BEN Form Service
 * Handles all data operations for W-8BEN forms with clean model separation
 */

import { W8BENFormModel, W8BENResponseModel, W8BENFieldMapper } from "../Models/W8BENModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class W8BENService {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create a W8BEN form model from API response data
   * Uses response model to form model mapping
   */
  static fromApiResponse(apiData: any): W8BENFormModel {
    // First create response model from API data
    const responseModel = new W8BENResponseModel(apiData);

    // Handle legacy API field mappings
    this.mapLegacyApiFields(apiData, responseModel);

    // Handle dynamic treaty fields
    this.mapTreatyFields(apiData, responseModel);

    // Map response model to form model
    return W8BENFieldMapper.mapResponseToForm(responseModel);
  }

  /**
   * Map legacy API field names to response model fields
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W8BENResponseModel): void {
    const legacyMappings: Record<string, string[]> = {
      "Beneficial Owner name": ["Beneficial_Owner_name", "beneficial_owner_name"],
      "First name and middle name": ["First_name_and_middle_name", "first_name"],
      "Last name": ["Last_name", "last_name"],
      "Date of birth": ["Date_of_birth", "date_of_birth", "birth_date"],
      "Country of citizenship": ["Country_of_citizenship", "citizenship_country"],
      "Permanent residence address": ["Permanent_residence_address", "permanent_address"],
      "Permanent city address": ["Permanent_city_address", "permanent_city"],
      "Mailing address": ["Mailing_address", "mailing_address"],
      "Mailing address city": ["Mailing_address_city", "mailing_city"],
      "U.S. taxpayer identification number": ["US_taxpayer_identification_number", "us_tin"],
      "Foreign tax identifying number": ["Foreign_tax_identifying_number", "foreign_tin"],
      Signature_date: ["Signature_date", "signature_date", "sign_date"],
      Signer_printed_name: ["Signer_printed_name", "signer_name"],
    };

    Object.entries(legacyMappings).forEach(([modelField, legacyFields]) => {
      if (!responseModel[modelField]) {
        for (const legacyField of legacyFields) {
          if (apiData[legacyField]) {
            responseModel[modelField] = apiData[legacyField];
            break;
          }
        }
      }
    });
  }

  /**
   * Map dynamic treaty fields from API response
   */
  private static mapTreatyFields(apiData: any, responseModel: W8BENResponseModel): void {
    // Handle indexed treaty fields
    Object.keys(apiData).forEach((apiField) => {
      if (
        apiField.includes("Treaty_country_") ||
        apiField.includes("Treaty_article_") ||
        apiField.includes("Treaty_withholding_rate_") ||
        apiField.includes("Treaty_income_type_") ||
        apiField.includes("Treaty_conditions_explanation_")
      ) {
        (responseModel as any)[apiField] = apiData[apiField];
      }
    });

    // Handle treatyClaims JSON string
    if (apiData.treatyClaims && typeof apiData.treatyClaims === "string") {
      try {
        const claims = JSON.parse(apiData.treatyClaims);
        (responseModel as any)["treatyClaims"] = apiData.treatyClaims;
      } catch (error) {
        console.warn("Failed to parse treatyClaims:", error);
      }
    }
  }

  /**
   * Convert form model to API payload format
   */
  static toApiPayload(model: W8BENFormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (value !== undefined && typeof value !== "function") {
        payload[field] = value;
      }
    });

    return payload;
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W8BENFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      let userData;

      // Try multiple authentication methods like working services
      try {
        userData = getUserIdFromLocalStorage();
        console.log("✅ getUserIdFromLocalStorage succeeded");
      } catch (error) {
        console.log("❌ getUserIdFromLocalStorage failed, trying alternative methods:", error);

        // Try method used by IMYService
        try {
          userData = JSON.parse(localStorage.getItem("userData") || "{}");
          console.log("✅ userData method succeeded");
        } catch (error2) {
          console.log("❌ userData method failed, trying token method:", error2);
          // Fallback to direct localStorage access
          const tokenData = localStorage.getItem("token");
          if (tokenData) {
            userData = JSON.parse(tokenData);
          }
        }
      }

      if (!userData || !userData.token) {
        console.error("❌ No authentication token found in any method");

        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          console.log(
            "🔍 W8BEN: Detected complex formsData structure - preserving withholdingData"
          );

          // Find and update the W8BEN form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (
                  dataItem &&
                  (dataItem.formType === "W8BEN" || dataItem.Form_number === "W-8BEN")
                ) {
                  console.log(`✅ W8BEN: Updating form at formsData[${i}].data[${j}]`);
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            console.log("⚠️ W8BEN: Could not find existing form, adding new one");
            parsedFormsData.formsData.push({
              request_id: "new-w8ben-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-w8ben-" + Date.now(),
                subRequestId: "new-w8ben-" + Date.now(),
                taxForms: "W8BEN",
                type: "extraction",
              },
            });
          }

          console.log("✅ W8BEN: Complex structure preserved with withholdingData intact");
        } else {
          // Handle as simple array
          console.log("🔍 W8BEN: Detected simple array structure");
          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index (or add if it doesn't exist)
          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            // Update existing form data by merging with new data
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            // Ensure array is large enough
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            // Add new form data
            formsDataArray[selectedIndex] = formData;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        console.log("W8BEN: Error parsing existing formsData, creating new array:", error);
        parsedFormsData = [formData];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      console.log("W8BEN Save operation completed successfully");
      return true;
    } catch (error) {
      console.error("Failed to save W-8BEN form:", error);
      throw error;
    }
  }
}
